import { useEffect, useRef } from "react";
import { CornerDownLeft } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import Message from "@/components/Message";
import { useStore } from "@/state/useState";
import { buildMessage } from "@/lib/api/utils";
import { prefersReducedMotion } from "@/lib/utils";

export default function ChatInterface() {
  const messages = useStore((state) => state.messages);
  const submitMessage = useStore((state) => state.submitMessage);
  const greeting = useStore((state) => state.config?.chat.greeting);
  const isLoadedFromHistory = useStore((state) => state.isLoadedFromHistory);
  const chatRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // On change of messages, scroll to the bottom.
  //
  // This is the most sustained motion in the app: it re-fires on every streamed
  // chunk, so a long answer scrolls continuously for as long as it takes to
  // generate. Under "Reduce motion" jump instead of gliding -- the user still
  // lands on the newest message, they are just not carried there (MA-213). A CSS
  // `scroll-behavior` rule cannot express this: an explicit `behavior` option
  // wins over the stylesheet.
  useEffect(() => {
    chatRef.current?.scrollTo({
      top: chatRef.current.scrollHeight,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const messageText = formData.get("message") as string;
    if (!messageText) return;

    e.currentTarget.reset();
    const message = buildMessage({
      message: messageText,
      role: "user",
    });

    submitMessage(message);
    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      formRef.current?.requestSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full bg-muted/50 rounded-xl border shadow-sm overflow-hidden lg:col-span-2">
      <div
        ref={chatRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 overflow-x-hidden"
      >
        {/* Sam's opening line. It opens every conversation started in this
            session and stays as the first bubble for the rest of it; a
            conversation reopened from the history list does not get one, since
            it was never part of that transcript.

            Deliberately static UI and NOT a store message: `submitMessage`
            posts the whole `messages` array, so a greeting held in the store
            would be persisted to Cosmos and re-fed to the model every turn --
            it would read its own words back as something it had said. As
            markup it costs no tokens and cannot alter an answer.

            Copy lives in the customer config (`chat.greeting`), so the wording
            is editable without a frontend release; customers without one
            simply get no greeting. */}
        {!isLoadedFromHistory && greeting && (
          <div className="flex justify-start mb-4">
            <div className="flex flex-row items-start w-3/4">
              <Avatar className="w-8 h-8 mr-2">
                <AvatarImage src="/sam-icon.svg" alt="Sam" />
                <AvatarFallback>AI</AvatarFallback>
              </Avatar>
              <Card className="w-full">
                <CardContent className="p-3">{greeting}</CardContent>
              </Card>
            </div>
          </div>
        )}
        {messages.map((message) => (
          <Message key={message.id} {...message} />
        ))}
      </div>
      <div className="p-4 bg-background border-t">
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-2">
          <div className="relative">
            <Label htmlFor="message" className="sr-only">
              Vraag
            </Label>
            <Textarea
              id="message"
              name="message"
              placeholder="Typ hier je vraag..."
              className="w-full resize-none border rounded-md p-3 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              rows={3}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" className="gap-1.5">
              Vraag verzenden
              <CornerDownLeft className="size-3.5" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
